import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import { Button } from '../../../components/common/atom/Button';
import { Text } from '../../../components/common/atom/Text';
import PageTemplate from '../../../components/common/template/PageTemplate';
import * as HeApi from '../HeApi';
import * as PATH from '../../../lib/routes/path';
import { ClipLoader } from 'react-spinners';
import { BarcodeDetector } from "barcode-detector";
import { BarcodeScanner } from 'react-barcode-scanner';

const HeRecordingBarcode = () => {
  const [ isLoading, setIsLoading ] = useState(false);
  
  const inputRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    inputRef.current.select();
  }, []);

  const onCaptureBarcode = useCallback((capture) => {
    const capturedBarcode = capture[0].rawValue;
    inputRef.current.value = formatValue(capturedBarcode);
    validateBarcode();
  }, []);

  // Format a string of digits into the pattern "0-000-000-000-000"
  const formatValue = (digits) => {
    // Limit to 13 digits
    const limitedDigits = digits.slice(0, 13);
    
    // Format the digits with hyphens
    let formatted = '';
    
    for (let i = 0; i < limitedDigits.length; i++) {
      // Add hyphen after positions 1, 4, 7, and 10
      if (i === 1 || i === 4 || i === 7 || i === 10) {
        formatted += '-';
      }
      formatted += limitedDigits[i];
    }
    
    return formatted;
  };

  // Extract only digits from formatted string
  const extractDigits = (formattedValue) => {
    return formattedValue.replace(/-/g, '');
  };

  // Handle paste events to strip non-numeric characters
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = (e.clipboardData || window.clipboardData).getData('text');
    const digits = pasteData.replace(/\D/g, '');
    
    if (digits && inputRef.current) {
      const currentInput = inputRef.current.value;
      const currentDigits = extractDigits(currentInput);
      const newDigits = currentDigits + digits;
      const formatted = formatValue(newDigits);
      
      // Save cursor position info
      const cursorPos = inputRef.current.selectionStart;
      const digitsBefore = currentInput.substring(0, cursorPos).replace(/-/g, '').length;
      const digitsAdded = digits.length;
      
      // Update input value
      inputRef.current.value = formatted;
      
      // Calculate new cursor position after paste
      let newPos = digitsBefore + digitsAdded;
      if (newPos > 0) newPos += 1;
      if (newPos > 3) newPos += 1;
      if (newPos > 6) newPos += 1;
      if (newPos > 9) newPos += 1;
      
      // Set cursor position
      inputRef.current.setSelectionRange(newPos, newPos);
    }
  };

  // Handle key down to prevent non-numeric input
  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, arrow keys, home, end
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (e.key === 'Enter') {
      validateBarcode();
    }
    
    if (allowedKeys.includes(e.key)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Cmd+A, Cmd+C, Cmd+V, Cmd+X
    if (['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()) && (e.ctrlKey || e.metaKey)) {
      return;
    }
    
    // Allow: numeric keys 0-9
    if (/^\d$/.test(e.key)) {
      // Check if adding this digit would exceed the 13-digit limit
      if (inputRef.current) {
        const currentDigits = extractDigits(inputRef.current.value);
        if (currentDigits.length >= 13) {
          e.preventDefault();
        }
      }
      return;
    }
    
    // Block all other keys
    e.preventDefault();
  };

  // Handle input changes (for when digits are added or removed)
  const handleInput = () => {
    if (inputRef.current) {
      // Get current cursor position
      const cursorPos = inputRef.current.selectionStart;
      
      // Get current input value
      const currentValue = inputRef.current.value;
      
      // Extract only digits
      const digits = currentValue.replace(/\D/g, '');
      
      // Format the digits
      const formattedValue = formatValue(digits);
      
      // Calculate new cursor position - this is complex due to the formatting
      let newCursorPos = cursorPos;
      
      // If the length changed due to formatting, adjust cursor position
      if (currentValue !== formattedValue) {
        // Count digits before cursor in current value
        const digitsBefore = currentValue.substring(0, cursorPos).replace(/-/g, '').length;
        
        // Calculate new position based on digits + hyphens
        newCursorPos = digitsBefore;
        if (newCursorPos > 0) newCursorPos += 1; // Add 1 for hyphen after first digit
        if (newCursorPos > 3) newCursorPos += 1; // Add 1 for hyphen after fourth digit
        if (newCursorPos > 6) newCursorPos += 1; // Add 1 for hyphen after seventh digit
        if (newCursorPos > 9) newCursorPos += 1; // Add 1 for hyphen after tenth digit
      }
      
      // Update input value directly
      inputRef.current.value = formattedValue;
      
      // Restore cursor position
      inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }
  };

  const validateBarcode = () => {
    if (isLoading) return;

    const inputBarcodeId = inputRef.current.value.replace(/-/g, '');
    const barcodeId = inputBarcodeId.substring(1);

    if (inputBarcodeId.length !== 13) {
      window.alert("유효하지 않은 바코드입니다.\n13자리 바코드 숫자를 다시 한번 확인해 주시기 바랍니다.");
      inputRef.current.value = '';
      localStorage.removeItem("he_recording_barcodeId");
      return;
    }

    if (inputBarcodeId[0] !== '0') {
      window.alert("유효하지 않은 바코드입니다.\n바코드 숫자가 올바른지 다시 한번 확인해 주시기 바랍니다.");
      inputRef.current.value = '';
      localStorage.removeItem("he_recording_barcodeId");
      return;
    }

    setIsLoading(true);
    HeApi.validateBarcode({ barcodeId }).then(response => {
      if (!response.data.valid) {
        window.alert("유효하지 않은 바코드입니다.\n바코드 숫자가 올바른지 다시 한번 확인해 주시기 바랍니다.");
        inputRef.current.value = '';
        localStorage.removeItem("he_recording_barcodeId");
        return;
      }
      
      if (!response.data.userAgree) {
        history.push(`${PATH.HE_RECORDING_BARCODE}/${barcodeId}/privacy-consent`, { 
          fromBarcode: true 
        });
      } else {
        localStorage.setItem("he_recording_barcodeId", barcodeId);
        history.push(`${PATH.HE_RECORDING_BARCODE}/${barcodeId}/list`, { 
          validated: true 
        });
      }
    }).catch(() => {
      window.alert("바코드 조회 중 오류가 발생하였습니다.\n다시 한번 시도해 주시기 바랍니다.");
      localStorage.removeItem("he_recording_barcodeId");
    }).finally(() => setIsLoading(false));
  }

  return (
    <PageTemplate title={PAGE_TITLE_BARCODE}>
      <div style={{ marginBottom: '0.625rem' }}>
        <Text size={16} lineHeight={1.5}>초음파 동영상 확인을 위해 바코드 숫자를 인식 또는 입력해 주세요</Text>
        <div style={{ margin: '0.625rem 0', width: '21rem', height: '15.7rem' }}>
          <BarcodeScanner options={{ formats: ['code_128'] }} onCapture={onCaptureBarcode} />
        </div>
        <FormElement>
          <Input type="input" 
            ref={inputRef}
            placeholder="예) 0-000-000-000-000" 
            maxLength={17}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            defaultValue={localStorage.getItem("he_recording_barcodeId") && formatValue('0' + localStorage.getItem("he_recording_barcodeId"))}
            autoComplete="off"
          />
        </FormElement>
        <div>
          {isLoading ? (
            <SubmitButton>
              <ClipLoader color={'#ffffff'} size={24} />
            </SubmitButton>
          ) : (
            <SubmitButton onClick={validateBarcode}>
              조회하기
            </SubmitButton>
          )}
        </div>
      </div> 
    </PageTemplate>
  );
};

const PAGE_TITLE_BARCODE = "초음파 동영상 바코드 입력"

export default React.memo(HeRecordingBarcode);

const FormElement = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.625rem 0;
`;

const SubmitButton = styled(Button)`
  border-radius: 0.375rem;
  margin-top: 0.625rem;
  padding: 1rem;
  background: ${(props) => props.theme.color.orangePink};
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  flex: ${(props) => props.flex};
  margin: 0.625rem 0;
  padding: 0.625rem 1rem;
  border-radius: 6px;
  border: 1px solid
    ${(props) =>
      props.active
        ? props.theme.color.orangePink
        : props.theme.color.pinkishGrey};
  background: ${(props) => props.theme.background[props.background]};

  & + & {
    margin-top: 0.688rem;
  }
`;